// src/components/Charts/LineChartProspectosAlumnos.js
import React, { useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

/**
 * Props:
 *  - prospectos: [{ mes, año, total_prospectos }]
 *  - alumnos:    [{ mes, año, total_alumnos }]
 *  - bajas:      [{ mes, año, total_bajas }]   // nuevo
 */
export default function LineChartProspectosAlumnos({
  prospectos = [],
  alumnos = [],
  bajas = [],
}) {
  // lee un total genérico: total_prospectos | total_alumnos | total_bajas | bajas | total
  const readTotal = (obj) =>
    Number(
      obj.total_prospectos ??
      obj.total_alumnos ??
      obj.total_bajas ??
      obj.bajas ??
      obj.total ??
      0
    );

  const normalize = (arr) =>
    (arr || []).map((it) => {
      const y = Number(it.año);
      const m = Number(it.mes);
      const mm = String(m).padStart(2, '0');
      return {
        key: `${y}-${mm}`,
        label: `Mes ${m}/${y}`,
        total: readTotal(it),
      };
    });

  const pros = normalize(prospectos);
  const alus = normalize(alumnos);
  const bajasN = normalize(bajas);

  const keys = useMemo(() => {
    const s = new Set([
      ...pros.map((x) => x.key),
      ...alus.map((x) => x.key),
      ...bajasN.map((x) => x.key),
    ]);
    return Array.from(s).sort(); // YYYY-MM asc
  }, [pros, alus, bajasN]);

  const labels = keys.map((k) => {
    const found = pros.find((p) => p.key === k) || alus.find((a) => a.key === k) || bajasN.find((b) => b.key === k);
    return found ? found.label : k;
  });

  const series = (src) => keys.map((k) => src.find((i) => i.key === k)?.total ?? 0);

  const data = {
    labels,
    datasets: [
      {
        label: 'Prospectos',
        data: series(pros),
        borderColor: '#3B82F6',
        backgroundColor: 'rgba(59,130,246,0.25)',
        borderWidth: 3,
        pointRadius: 4,
        tension: 0.35,
      },
      {
        label: 'Alumnos',
        data: series(alus),
        borderColor: '#F59E0B',
        backgroundColor: 'rgba(245,158,11,0.25)',
        borderWidth: 3,
        pointRadius: 4,
        tension: 0.35,
      },
      // NUEVA SERIE: BAJAS (línea roja punteada)
      {
        label: 'Bajas',
        data: series(bajasN),
        borderColor: '#EF4444',
        backgroundColor: 'rgba(239,68,68,0.20)',
        borderWidth: 3,
        pointRadius: 4,
        tension: 0.35,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top', labels: { usePointStyle: true } },
      title: { display: true, text: 'Prospectos, Alumnos y Bajas por Mes' },
      tooltip: { mode: 'index', intersect: false },
    },
    interaction: { mode: 'index', intersect: false },
    scales: {
      y: { beginAtZero: true, ticks: { precision: 0 } },
      x: { ticks: { autoSkip: true } },
    },
  };

  return (
    <div style={{ height: 340 }}>
      <Line data={data} options={options} />
    </div>
  );
}
